import { useEffect } from 'react';

import { BusinessSubscriptionModel, billingApi, profileApi } from '@pukka/api';
import { clickstreamTracker } from '@pukka/clickstream';
import { animationUtil, assertUtil, dateUtils, promiseUtils } from '@pukka/common/utils';
import { queryClient } from '@pukka/tanstack-query';
import { toast } from '@pukka/toast';
import { SelectValue } from '@pukka/ui-components';
import { proxy, subscribeKey } from '@pukka/valtio';
import { add } from 'date-fns';
import maxBy from 'lodash/maxBy';

import { BillingPersistedState, BillingState } from '../Billing.state';
import { createDiscountDescriptionFromCoupon, getIntervalFromMonth } from '../utils';

interface Amount {
  readonly id: number;
  readonly name: string;
  originalPrice: number;
  finalPrice: number;
  unit: string;
  isSelected: boolean;
  description?: string; // -> package deal display name
  prorateInfo?: string;
}

type Cycle = 1 | 12;
type PaymentType = 'recurring' | 'regular';

interface IPaymentState {
  isLoading: boolean;
  isPaying: boolean | 'debit' | 'credit';
  isApplyingCoupon: boolean;
  isTnCModalShown: boolean;
  isCycleUserAmended: boolean;
  type: PaymentType;
  cycle: Cycle;
  cycles: SelectValue<Cycle, string, true>[];
  couponCode: string;
  tncAccepted: boolean;
  isEnterprise: boolean;
  isMokaposUser: boolean;
  businessId?: number;
  activeOutletsCount: number;
  unusedOutletsMessage: string;
  isPaymentGatewayError: boolean;
  isCycleDiscountSchemeModalShown: boolean;
  hasCycleDiscountScheme: boolean;
  cycleDiscountScheme: BusinessSubscriptionModel.RecurringBillingCycle[];
  verifyCouponRequest: Parameters<(typeof billingApi)['verifyBillingCoupon']>[1] | null;
  recurringDiscountMessage: string;
  amount: {
    discountId: number | undefined;
    employeeSlots: {
      count: number;
      originalPrice: number;
      finalPrice: number;
      unit: string;
    };
    outlets: Amount[];
    coupon: {
      applied: boolean;
      amount: number;
      description: string;
      monthExtension?: number;
      error?: string;
    };
    total: {
      originalPrice: number;
      finalPrice: number;
      description: string;
      newExpiry: string;
    };
  };
  readonly hasActiveOutlet: boolean;
  readonly isPayable: boolean;
  readonly isPayableViaDirectDebit: boolean;
  readonly isPayableViaCreditCard: boolean;
  updateDiscountMessage(): void;
  onToggleCycleDiscountSchemeModal(): void;
  onChangePaymentType(type: PaymentType): void;
  onChangeBillingCycle(type: Cycle): void;
  onChangeCoupon(code: string): void;
  onApplyCoupon(): Promise<void>;
  onRemoveCoupon(): void;
  onToggleTnC(): void;
  onToggleOutletSelection(outletId: number): void;
  onClosePaymentGatewayErrorModal(): void;
  onPay(): void;
  onPayWithDirectDebit(): void;
  onPayWithCreditCard(): void;
  onUpdateCalculations(): void;
  onToggleTnCModal(): void;
  useBillingData(): void;
}

interface RenewalTrackerData {
  merchant: {
    businessId: number | undefined;
  };
  billing: {
    cycle: string;
    paymentAmount: number;
    totalOutlet: number;
  };
}

function pushRenewalClickstream(trackerData: RenewalTrackerData, locationHash: string) {
  switch (locationHash) {
    case '#fromPopup':
      clickstreamTracker.trackBilling('MOKAPOS_RENEWAL_FULL_FLOW', trackerData);
      break;
    case '#fromBanner':
      clickstreamTracker.trackBilling('MOKAPOS_RENEWAL_BANNER_CLICK', trackerData);
      break;
    default:
      clickstreamTracker.trackBilling('MOKAPOS_RENEWAL_BILLING_PAY', trackerData);
  }
}

const PaymentState: IPaymentState = proxy({
  isLoading: false,
  isPaying: false,
  isApplyingCoupon: false,
  isEnterprise: false,
  isMokaposUser: false,
  businessId: undefined,
  isTnCModalShown: false,
  isCycleUserAmended: false,
  type: 'regular',
  cycle: 1,
  cycles: [] as SelectValue<Cycle, string, true>[],
  couponCode: '',
  tncAccepted: false,
  activeOutletsCount: 0,
  unusedOutletsMessage: '',
  isPaymentGatewayError: false,
  isCycleDiscountSchemeModalShown: false,
  verifyCouponRequest: null,
  recurringDiscountMessage: '',
  amount: {
    discountId: undefined,
    employeeSlots: {
      count: 0,
      originalPrice: 0,
      finalPrice: 0,
      unit: '',
    },
    outlets: [],
    coupon: {
      applied: false,
      amount: 0,
      description: '',
    },
    total: {
      originalPrice: 0,
      finalPrice: 0,
      description: '',
      newExpiry: '',
    },
  },
  cycleDiscountScheme: [],
  get hasCycleDiscountScheme() {
    return PaymentState.cycleDiscountScheme.length > 0;
  },
  get hasActiveOutlet() {
    return PaymentState.activeOutletsCount > 0;
  },
  get isPayable() {
    return (
      PaymentState.isMokaposUser &&
      PaymentState.hasActiveOutlet &&
      !BillingPersistedState.pendingPaymentMessage &&
      !PaymentState.unusedOutletsMessage
    );
  },
  get isPayableViaDirectDebit() {
    return PaymentState.isPayable && PaymentState.tncAccepted;
  },
  get isPayableViaCreditCard() {
    // TODO: Open this
    // return (
    //   PaymentState.isPayable &&
    //   PaymentState.tncAccepted
    // );
    return false;
  },
  updateDiscountMessage() {
    const recurringCalculation = BillingState.calculations.filter((calc) => calc.is_recurring && calc.month === PaymentState.cycle);
    const biggestDiscount = maxBy(recurringCalculation, (calc) => calc.discount_percentage)?.discount_percentage;

    PaymentState.recurringDiscountMessage = biggestDiscount
      ? `Get more discount up to ${biggestDiscount}% for this transaction by subscribing to recurring ${getIntervalFromMonth(
          PaymentState.cycle,
        )}.`
      : '';
  },
  onUpdateCalculations() {
    PaymentState.cycles = BillingState.calculations
      .filter((d) => (PaymentState.type === 'recurring' ? d.is_recurring : !d.is_recurring))
      .map((d) => ({
        key: d.month as Cycle,
        value: d.subscription_name ?? '',
      }));

    if (PaymentState.cycles.length > 0 && PaymentState.cycles.findIndex((c) => c.key === PaymentState.cycle) === -1) {
      PaymentState.cycle = PaymentState.cycles[0].key as Cycle;
    }

    const data = BillingState.calculations
      .filter((d) => (PaymentState.type === 'recurring' ? d.is_recurring : !d.is_recurring))
      .find((d) => d.month === PaymentState.cycle);

    if (!data) {
      return;
    }

    const extendedDate =
      PaymentState.amount.coupon.monthExtension && data.extended_date
        ? add(new Date(data.extended_date), {
            months: PaymentState.amount.coupon.monthExtension,
          })
        : data.extended_date;

    PaymentState.amount.outlets =
      data.outlets?.map((outlet) => ({
        id: outlet.outlet_id ?? -1,
        name: outlet.outlet_name ?? '',
        originalPrice: data.original_package_deal_price_per_month ?? 0,
        finalPrice: data.final_package_deal_price_per_month ?? 0,
        isSelected: outlet.is_active_subscription ?? false,
        description: data.package_deal_display_name,
        prorateInfo: outlet.is_prorated
          ? `Prorated From: ${dateUtils.formatDate(outlet.current_end_date, 'dd/MM/yyyy')} to ${dateUtils.formatDate(
              extendedDate,
              'dd/MM/yyyy',
            )}`
          : '',
        unit: '/mo',
      })) ?? [];

    PaymentState.activeOutletsCount = PaymentState.amount.outlets.filter((o) => o.isSelected).length;

    PaymentState.amount.discountId = data.subscription_discount_id;

    PaymentState.amount.employeeSlots = {
      count: data.employee_slot?.count ?? 0,
      originalPrice: data.employee_slot?.original_price ?? 0,
      finalPrice: data.employee_slot?.final_price ?? 0,
      unit: '/slot/mo',
    };

    PaymentState.amount.total = {
      finalPrice: data.final_total_price ?? 0,
      originalPrice: data.original_total_price ?? 0,
      description: `Discount ${data.discount_percentage}%${
        data.discount_valid_until ? `\npay before ${dateUtils.formatDate(data.discount_valid_until, 'd MMMM yyyy')}` : ''
      }`,
      newExpiry: PaymentState.isMokaposUser && extendedDate ? dateUtils.formatDate(extendedDate) : '',
    };

    PaymentState.verifyCouponRequest = data.verify_coupon_request;

    PaymentState.cycleDiscountScheme = data.recurring_billing_cycles ?? [];
  },
  onToggleTnCModal() {
    PaymentState.isTnCModalShown = !PaymentState.isTnCModalShown;
  },
  onChangePaymentType(type) {
    animationUtil.doTransition(() => {
      // reset applied coupon
      PaymentState.couponCode = '';
      PaymentState.amount.coupon = {
        applied: false,
        amount: 0,
        description: '',
      };

      PaymentState.type = type;
      PaymentState.tncAccepted = false;
    });
  },
  async onChangeBillingCycle(cycle) {
    // reset applied coupon
    PaymentState.couponCode = '';
    PaymentState.amount.coupon = {
      applied: false,
      amount: 0,
      description: '',
    };

    PaymentState.isCycleUserAmended = true;
    PaymentState.cycle = cycle;
  },
  onChangeCoupon(code) {
    PaymentState.couponCode = code.toUpperCase();
  },
  onToggleCycleDiscountSchemeModal() {
    PaymentState.isCycleDiscountSchemeModalShown = !PaymentState.isCycleDiscountSchemeModalShown;
  },
  async onApplyCoupon() {
    const code = PaymentState.couponCode;

    PaymentState.isApplyingCoupon = true;

    try {
      const {
        data: { benefit },
      } = await billingApi.verifyBillingCoupon(code, PaymentState.verifyCouponRequest || undefined);

      PaymentState.couponCode = code;

      PaymentState.amount.coupon = {
        applied: true,
        amount: benefit.total_discount_amount,
        description: createDiscountDescriptionFromCoupon(benefit.total_discount_percentage, benefit.additional_month, code),
        monthExtension: benefit.additional_month,
      };

      PaymentState.onUpdateCalculations();

      toast.success('Coupon Code successfully applied.');
    } catch (e: unknown) {
      assertUtil.assertErrorHasResponse<{
        data?: { errors?: string[] };
      }>(e);

      const errorMessage = e.response.data?.errors?.[0];

      if (errorMessage?.includes('subscription') || errorMessage === 'Invalid code, price error') {
        PaymentState.amount.coupon = {
          applied: false,
          amount: 0,
          description: '',
          error: errorMessage,
        };
      } else {
        PaymentState.amount.coupon = {
          applied: false,
          amount: 0,
          description: '',
          error: 'Invalid Coupon Code',
        };
      }
    } finally {
      PaymentState.isApplyingCoupon = false;
    }
  },
  async onRemoveCoupon() {
    PaymentState.couponCode = '';
    PaymentState.isApplyingCoupon = true;

    await promiseUtils.postpone();

    PaymentState.amount.coupon = {
      applied: false,
      amount: 0,
      description: '',
    };

    PaymentState.onUpdateCalculations();

    PaymentState.isApplyingCoupon = false;
  },
  onToggleTnC() {
    PaymentState.tncAccepted = !PaymentState.tncAccepted;
  },
  onToggleOutletSelection(outletId) {
    const outlet = PaymentState.amount.outlets.find((o) => o.id === outletId);

    if (outlet) {
      outlet.isSelected = !outlet.isSelected;

      PaymentState.isLoading = true;

      billingApi
        .updateOutletSubscription({
          id: outletId,
          isActive: outlet.isSelected,
        })
        .then((res) => {
          const eventName = res.data?.is_active_subscription
            ? 'MOKAPOS_TURN_ON_SUBSCRIPTION_OUTLET'
            : 'MOKAPOS_TURN_OFF_SUBSCRIPTION_OUTLET';

          clickstreamTracker.trackBilling(eventName, {
            merchant: {
              businessId: res.data?.business_id,
            },
            billing: {
              featureName: res.data?.name,
              subscribeExpiredDate: res.data?.end_date,
            },
          });

          queryClient.invalidateQueries({ queryKey: ['billings-calculation'] });
        })
        .catch((err) => {
          assertUtil.assertErrorIsError(err);

          outlet.isSelected = !outlet.isSelected;

          toast.error(err.message);
        })
        .finally(() => {
          PaymentState.isLoading = false;
        });
    }
  },
  onClosePaymentGatewayErrorModal() {
    PaymentState.isPaymentGatewayError = false;
  },
  async onPay() {
    PaymentState.isPaying = true;

    if (PaymentState.couponCode && !PaymentState.amount.coupon.applied) {
      await PaymentState.onApplyCoupon();
    }

    const totalAmount = PaymentState.amount.total.finalPrice - PaymentState.amount.coupon.amount;

    const billingTrackerData = {
      cycle: PaymentState.cycle === 1 ? 'Monthly' : 'Annual',
      paymentAmount: totalAmount,
      totalOutlet: PaymentState.activeOutletsCount,
    };

    clickstreamTracker.trackBilling('MOKAPOS_SETTING_BILLING_PAY', {
      billing: billingTrackerData,
    });

    const renewalTrackerData = {
      merchant: {
        businessId: PaymentState.businessId,
      },
      billing: billingTrackerData,
    };

    pushRenewalClickstream(renewalTrackerData, window.location.hash);

    try {
      const res = await billingApi.verifyBilling({
        subscription_discount_id: PaymentState.amount.discountId,
        final_price: totalAmount,
        coupon_code: PaymentState.couponCode,
      });

      if (res.redirect_url) {
        clickstreamTracker.trackBilling('MOKAPOS_BILLING_PAYMENT_REDIRECT_TO_MIDTRANS', {
          billing: billingTrackerData,
        });

        window.location.href = res.redirect_url;
      }
    } catch (error: unknown) {
      PaymentState.isPaying = false;
      assertUtil.assertObjectIsTruthy<{
        data:
          | string
          | {
              message?: string;
              status: string;
            };
        status: number;
      }>(error);

      if (!error.data || error.data === ' ' || error.status >= 500) {
        PaymentState.isPaymentGatewayError = true;
      } else if (typeof error.data !== 'string') {
        toast.error(error.data.message ?? error.data.status);
      }
    }
  },
  async onPayWithDirectDebit() {
    PaymentState.isPaying = 'debit';

    clickstreamTracker.trackComponent('MOKAPOS_RECURRING_DEBIT');

    try {
      const { url } = await billingApi.subscribeRecurringPayment({
        payment_type: 'DEBIT',
        final_amount: PaymentState.amount.total.finalPrice - PaymentState.amount.coupon.amount,
        subscription_discount_id: PaymentState.amount.discountId,
        coupon_code: PaymentState.couponCode,
        tnc_approve_date: new Date().toISOString(),
      });

      BillingState.onChangeIsPayingRecurring(true);

      if (url) {
        window.location.href = url;
      }
    } catch (error: unknown) {
      PaymentState.isPaying = false;

      queryClient.invalidateQueries({ queryKey: ['billing-active-recurring'] });

      assertUtil.assertObjectIsTruthy<{
        data:
          | string
          | {
              error?: string;
              status: string;
            };
        status: number;
      }>(error);

      toast.error(typeof error.data === 'string' ? error.data : error.data.error ?? error.data.status);
    }
  },
  /* istanbul ignore next */
  async onPayWithCreditCard() {
    PaymentState.isPaying = 'credit';

    // TODO: Integrate with API
    await promiseUtils.postpone();

    PaymentState.isPaying = false;
  },
  useBillingData() {
    const { status: profileStatus, data: profile } = profileApi.useProfile();

    useEffect(() => {
      if (profileStatus === 'success') {
        if (!PaymentState.isCycleUserAmended) {
          PaymentState.cycle = profile.business?.current_plan as Cycle;
        }

        PaymentState.isMokaposUser = !!profile.business?.is_mokapos_user;
        PaymentState.isEnterprise = !!profile.business?.is_enterprise;
        PaymentState.businessId = profile.business?.id;

        const unusedOutletsSlot = (profile.business?.outlet_slot ?? 0) - (profile.outlets || []).length;

        PaymentState.unusedOutletsMessage =
          unusedOutletsSlot > 0
            ? `You still have slots for additional ${unusedOutletsSlot} outlets. Please create the outlets before making a payment.`
            : '';
      }
    }, [profileStatus, profile]);
  },
});

subscribeKey(PaymentState, 'cycle', PaymentState.onUpdateCalculations);
subscribeKey(PaymentState, 'cycle', PaymentState.updateDiscountMessage);
subscribeKey(BillingState, 'calculations', PaymentState.onUpdateCalculations);
subscribeKey(BillingState, 'calculations', PaymentState.updateDiscountMessage);
subscribeKey(PaymentState, 'type', PaymentState.onUpdateCalculations);

export { PaymentState, type PaymentType, type Cycle, type IPaymentState, pushRenewalClickstream as _pushRenewalClickstream };
